import { defineStore } from 'pinia';
import { Messaging, deleteToken } from 'firebase/messaging';
import { api, erebus } from '../boot/axios';
import { useAuthStore } from './auth';
import {
  DeleteCaseReminderResponse,
  INotification,
  INotificationResponse,
  VapidResponse,
} from 'src/models/notifications';
import { ErebusNotification } from 'src/models/notifications';

/** How many notifications one "Load more" brings in, per panel. */
export const NOTIFICATION_PAGE_SIZE = 20;

/**
 * Where the next page starts: the createdAt and ID of the last item loaded.
 * createdAt is the server's own string. A JS Date keeps only milliseconds, and
 * a rounded cursor would skip rows created in the same millisecond.
 */
interface NotificationCursor {
  before: string;
  beforeId: string;
}

/** The body of GET /notifications/page. */
interface NotificationPageResponse {
  items: INotificationResponse[];
  unreadCount: number;
}

interface NotificationPage {
  items: INotification[];
  cursor: NotificationCursor | null;
  hasMore: boolean;
}

export const useNotificationStore = defineStore('notification', {
  persist: false,

  state: () => ({
    notifications: [] as INotification[],
    unreadCount: 0,
    unreadCursor: null as NotificationCursor | null,
    readCursor: null as NotificationCursor | null,
    unreadHasMore: false,
    readHasMore: false,
    erebusToken: '' as string,
    notificationPermissions: false,
    messagingInstance: null as Messaging | null,
    notificationsAreLoading: false,
  }),

  getters: {
    notificationsEnabled: (state) => {
      return state.erebusToken !== '' && state.notificationPermissions;
    },
    userNotifications: (state) => {
      return state.notifications;
    },
  },

  actions: {
    async fetchNotificationsSinceDate(date: Date): Promise<INotification[]> {
      const timestamp = date.toISOString();
      const res = await erebus.get<INotificationResponse[]>(`/notifications/since/${timestamp}`);
      if (res.data.length === 0) {
        return [];
      }

      return res.data.map((response) => {
        return new ErebusNotification(response);
      });
    },
    async handleLogin(): Promise<void> {
      try {
        // // First check if we're in a browser that supports Notification API
        if (typeof Notification === 'undefined') {
          console.log('Notifications not supported in this environment');
          return;
        }

        if (Notification.permission === 'denied') {
          console.log('Notifications denied by user');
          return;
        } else if (Notification.permission === 'granted') {
          this.notificationPermissions = true;
          console.log('Notifications already granted');
          return;
        }

        try {
          const res = await Notification.requestPermission();
          if (res === 'granted') {
            this.notificationPermissions = true;
            console.log('Notifications permission granted');
          } else {
            console.log('Notifications permission response:', res);
          }
        } catch (permissionError) {
          console.error('Error requesting notification permission:', permissionError);
          // Don't throw, just continue without notifications
          return;
        }
      } catch (e) {
        console.error('handleLogin error:', e);
        // Don't throw the error - allow the login process to continue
        // even if notifications fail
      }
    },
    async getVapidKey(): Promise<string> {
      const res = await erebus.get<VapidResponse>('/devices/vapid');
      return res.data.vapidKey;
    },
    async registerErebusToken(token: string): Promise<void> {
      await erebus.post(`/devices/register/${token}`);
      this.erebusToken = token;
    },
    /**
     * Unregisters this device for push: tells Go, and tells Firebase.
     *
     * Firebase runs on the side and is never waited for. Its call has no
     * timeout, and cookie-mode logout waits for this before it ends the Go
     * session (PRX-1745).
     *
     * @param timeoutMs - Cap for the Go request. Unset uses the app default.
     */
    async deleteErebusToken(timeoutMs?: number): Promise<void> {
      if (this.erebusToken === '') {
        return;
      }

      if (this.messagingInstance === null) {
        throw new Error('Messaging instance is null');
      }

      // Logout clears the auth token before the axios interceptors run, so the
      // token is captured now and sent as this request's own header.
      const authToken = useAuthStore().token;
      const deviceToken = this.erebusToken;
      // Cleared before the request, so a second call finds nothing to delete.
      // A 401 here logs out again, and that would otherwise loop forever.
      this.erebusToken = '';
      const messaging = this.messagingInstance;
      try {
        await erebus.delete(`/devices/delete/${deviceToken}`, {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
          ...(timeoutMs !== undefined && { timeout: timeoutMs }),
        });
      } finally {
        // Even if the server call fails, stop this browser getting pushes.
        // Started from a resolved promise, so a throw is caught the same way.
        Promise.resolve()
          .then(() => deleteToken(messaging))
          .catch(() => {
            console.warn('Could not stop push notifications in this browser');
          });
      }
    },
    /** Reloads the first page of each panel and the unread count. */
    async fetchNotifications(): Promise<void> {
      const [unread, read] = await Promise.all([
        this.fetchPage(false, null),
        this.fetchPage(true, null),
      ]);

      this.notifications = [...unread.items, ...read.items];
      this.unreadCursor = unread.cursor;
      this.unreadHasMore = unread.hasMore;
      this.readCursor = read.cursor;
      this.readHasMore = read.hasMore;
    },
    async fetchMoreUnread(): Promise<void> {
      const page = await this.fetchPage(false, this.unreadCursor);
      this.mergeNotifications(page.items);
      this.unreadCursor = page.cursor ?? this.unreadCursor;
      this.unreadHasMore = page.hasMore;
    },
    async fetchMoreRead(): Promise<void> {
      const page = await this.fetchPage(true, this.readCursor);
      this.mergeNotifications(page.items);
      this.readCursor = page.cursor ?? this.readCursor;
      this.readHasMore = page.hasMore;
    },
    /** Fetches one page. Every page carries the unread total, so the badge is refreshed too. */
    async fetchPage(read: boolean, cursor: NotificationCursor | null): Promise<NotificationPage> {
      const res = await erebus.get<NotificationPageResponse>('/notifications/page', {
        params: { read, limit: NOTIFICATION_PAGE_SIZE, ...cursor },
      });
      const { items, unreadCount } = res.data;
      const last = items[items.length - 1];
      this.unreadCount = unreadCount;

      return {
        items: items.map((notification) => new ErebusNotification(notification)),
        cursor: last ? { before: last.createdAt, beforeId: last.ID } : null,
        // A full page may still be the last one. The next load then returns
        // nothing and hides the button.
        hasMore: items.length === NOTIFICATION_PAGE_SIZE,
      };
    },
    async fetchUnreadCount(): Promise<void> {
      const res = await erebus.get<{ count: number }>('/notifications/unread/count');
      this.unreadCount = res.data.count;
    },
    /**
     * Adds notifications that the poll found. Ones already loaded are skipped,
     * so a repeat poll does not count the same notification twice.
     */
    addNewNotifications(notifications: INotification[]): void {
      const known = new Set(this.notifications.map((notification) => notification.ID));
      const added = notifications.filter((notification) => !known.has(notification.ID));
      this.notifications = [...added, ...this.notifications];
      this.unreadCount += added.filter((notification) => !notification.read).length;
    },
    /**
     * Adds a loaded page to the list. An item marked read after the first load
     * can come back in a read page, so items already present are replaced, not added twice.
     */
    mergeNotifications(notifications: INotification[]): void {
      const byID = new Map(
        this.notifications.map((notification) => [notification.ID, notification]),
      );
      for (const notification of notifications) {
        byID.set(notification.ID, notification);
      }
      this.notifications = [...byID.values()];
    },
    async markNotificationRead(notificationID: string): Promise<void> {
      const res = await erebus.put(`/notifications/read/${notificationID}`);
      if (res.status !== 200) {
        throw new Error(`Failed to mark notification ${notificationID} as read`);
      }
      const wasUnread = this.notifications.some(
        (notification) => notification.ID === notificationID && !notification.read,
      );
      this.markReadLocally((notification) => notification.ID === notificationID);
      if (wasUnread) {
        this.unreadCount = Math.max(0, this.unreadCount - 1);
      }
    },
    async markCaseNotificationsRead(caseID: number): Promise<void> {
      const res = await erebus.put(`/notifications/read/case/${caseID}`);
      if (res.status !== 200) {
        throw new Error(`Failed to mark case ${caseID} notifications as read`);
      }
      this.markReadLocally((notification) => notification.caseID === caseID);
      // The server also marks unread rows on pages not loaded yet, so the
      // local list cannot say how many were cleared.
      await this.fetchUnreadCount();
    },
    async markAllNotificationsRead(): Promise<void> {
      const res = await erebus.put('/notifications/read/all');
      if (res.status !== 200) {
        throw new Error('Failed to mark all notifications as read');
      }
      this.markReadLocally(() => true);
      this.unreadCount = 0;
      this.unreadCursor = null;
      this.unreadHasMore = false;
    },
    async deleteReadNotifications(): Promise<void> {
      await erebus.delete('/notifications/read');
      this.notifications = this.notifications.filter((notification) => !notification.read);
      this.readCursor = null;
      this.readHasMore = false;
    },
    async deleteNotification(notificationID: string): Promise<void> {
      const res = await erebus.delete(`/notifications/${notificationID}`);
      if (res.status !== 200) {
        throw new Error(`Failed to delete notification ${notificationID}`);
      }
      const wasUnread = this.notifications.some(
        (notification) => notification.ID === notificationID && !notification.read,
      );
      if (wasUnread) {
        this.unreadCount = Math.max(0, this.unreadCount - 1);
      }
      this.notifications = this.notifications.filter(
        (notification) => notification.ID !== notificationID,
      );
    },
    /**
     * Apply a read that the server has already accepted to the local copy.
     *
     * The list is a megabyte for some users, so the alternative - refetching it
     * after every read - is the expensive thing this avoids. readAt is set to
     * now, which is close enough for display; the server holds the real value
     * and sends it on the next full fetch.
     */
    markReadLocally(matches: (notification: INotification) => boolean): void {
      const readAt = new Date();
      this.notifications = this.notifications.map((notification) =>
        matches(notification) && !notification.read
          ? { ...notification, read: true, readAt }
          : notification,
      );
    },
    async deletePatientRecordReminder(
      patientRecordID: number,
    ): Promise<DeleteCaseReminderResponse> {
      const res = await api.delete(`/api/v2/notifications/reminders/${patientRecordID}`);
      if (res.status !== 200) {
        throw new Error(`Failed to delete reminder for patient record ${patientRecordID}`);
      }
      return new DeleteCaseReminderResponse(res.data.status);
    },
    async dataRefresh(): Promise<void> {
      await this.fetchNotifications();
    },
  },
});
